/**
 * Action filter utilities for DataTable components
 * Contains business logic for determining which actions should be shown
 */

/**
 * Filters actions based on row data and business rules
 * @param {Array} actions - Array of action objects
 * @param {Object} row - Row data object
 * @returns {Array} Filtered actions array
 */
export const filterActionsByRow = (actions, row) => {
  if (!actions || !Array.isArray(actions)) {
    return [];
  }

  return actions.filter(action => {
    switch (action.key) {
      case 'submit':
        // Show submit button if crawled is true
        return row.crawled === true;
      
      case 'checkWebsite':
        // Only show check website button if not yet crawled
        if (row.crawled === true) {
          return false;
        }
        return true;
      
      case 'contacts':
        // Only show contacts button if contacts field has actual data
        if (!row.contacts || row.contacts === '' || row.contacts.trim() === '') {
          return false;
        }
        // Try to parse and check if there's actual contact data
        try {
          const contactsData = typeof row.contacts === 'string' 
            ? JSON.parse(row.contacts) 
            : row.contacts;
          
          // Check if emails or phones arrays have content
          const hasEmails = contactsData?.emails && contactsData.emails.length > 0;
          const hasPhones = contactsData?.phones && contactsData.phones.length > 0;
          
          return hasEmails || hasPhones;
        } catch {
          // If parsing fails, fall back to checking if it's a non-empty string
          return true;
        }
      
      case 'delete':
      case 'archive':
      case 'undelete':
      case 'unarchive':
        // These actions are always shown (no filtering)
        return true;
      
      default:
        // For any other actions, show them by default
        return true;
    }
  });
};

/**
 * Gets secondary actions (delete, archive, contacts)
 * @param {Array} actions - Array of action objects
 * @param {Object} row - Row data object
 * @returns {Array} Filtered secondary actions
 */
export const getSecondaryActions = (actions, row) => {
  return filterActionsByRow(actions, row).filter(action => 
    !['submit', 'checkWebsite'].includes(action.key)
  );
};

/**
 * Gets primary actions (submit and checkWebsite - only one shows at a time)
 * @param {Array} actions - Array of action objects
 * @param {Object} row - Row data object
 * @returns {Array} Filtered primary actions
 */
export const getPrimaryActions = (actions, row) => {
  const filtered = filterActionsByRow(actions, row);
  const primaryActions = [];
  
  // Show checkWebsite when not crawled, submit when crawled
  const checkWebsiteAction = filtered.find(action => action.key === 'checkWebsite');
  if (checkWebsiteAction) {
    primaryActions.push(checkWebsiteAction);
  }
  
  const submitAction = filtered.find(action => action.key === 'submit');
  if (submitAction) {
    primaryActions.push(submitAction);
  }
  
  return primaryActions;
};
